import { typographyTokens } from "./typography.js";
import { colorsFoundation } from "./colors-foundation.js";
import { dimensions, borderRadius } from "./dimensions.js";
import { hexToRgba } from "../utils/colors.js";

// Component-level tokens bridge foundational/semantic tokens with concrete UI parts.
// These tokens group recurring styling decisions so components can stay DRY
// while still referencing design system primitives.
export const componentTokens = {
  // Dark sidebar (see the click dummy). Neutral greys are shared across skins
  // and read well on any dark surface; the active state derives from the brand
  // primary scale so it recolours per skin.
  sidebar: {
    title: colorsFoundation.neutral.white,
    item: {
      fontSize: typographyTokens.size.sm, // 13-14px, small
      icon: colorsFoundation.gray[500], // inactive icon
      text: colorsFoundation.gray[400], // inactive label
      hoverBg: "rgba(255,255,255,0.06)",
      activeBg: hexToRgba(colorsFoundation.key.blue[500], 0.15),
      activeIcon: colorsFoundation.key.blue[500],
      activeText: colorsFoundation.key.blue[300],
      paddingY: "9px",
      paddingX: "12px",
      marginX: "8px",
      borderRadius: borderRadius.xs, // 8px
      gap: "10px",
    },
  },
  tabList: {
    colors: {
      label: colorsFoundation.key.blue[700],
      indicatorActive: colorsFoundation.key.gold[600],
      indicatorInactive: colorsFoundation.neutral[300],
    },
    tabs: {
      minHeight: dimensions["5xl"],
      gap: dimensions["xl"],
    },
    indicator: {
      activeHeight: "0.1875rem", // 3px for active tab
      inactiveHeight: dimensions["px"], // 1px for inactive tabs
      borderRadius: borderRadius.xs,
      widthOffset: dimensions["2xl"], // subtract 1.5rem total (0.75rem inset per side)
    },
    tab: {
      paddingX: dimensions.lg,
      minHeight: dimensions["5xl"],
      fontSize: typographyTokens.size.base,
      fontWeight: {
        default: typographyTokens.weight.semibold,
        light: typographyTokens.weight.medium,
      },
      lineHeight: 1.5,
      hoverBackground: "transparent",
    },
    label: {
      gap: dimensions.md,
    },
    badge: {
      // 22px circle used for count indicators in TabList
      size: "1.375rem",
      fontSize: typographyTokens.size.xs,
      fontWeight: typographyTokens.weight.medium,
      backgroundColor: colorsFoundation.neutral[100],
      textColor: colorsFoundation.neutral[700],
    },
  },
  userMenu: {
    backgroundColor: colorsFoundation.neutral[50],
    arrowBackgroundColor: colorsFoundation.neutral[50],
    itemPadding: "12px 24px", // px: 3 in MUI spacing
    icon: {
      size: "1.5rem",
      color: colorsFoundation.key.blue[700],
    },
    text: {
      color: colorsFoundation.key.blue[700],
      fontWeight: typographyTokens.weight.semibold,
    },
  },
  dialog: {
    paddingX: dimensions["3xl"],
  },
  formSection: {
    backgroundColor: colorsFoundation.neutral[50],
    borderRadius: borderRadius.sm,
    padding: dimensions["2xl"],
  },
  uploadDropzone: {
    borderRadius: borderRadius.sm,
    padding: dimensions["2xl"],
    border: {
      default: colorsFoundation.neutral[700],
      drag: colorsFoundation.status.positive[600],
    },
    background: {
      default: colorsFoundation.neutral.white,
      dragging: colorsFoundation.status.positive[100],
      disabled: colorsFoundation.neutral[50],
    },
    text: {
      color: colorsFoundation.neutral[700],
    },
  },
  input: {
    height: dimensions["6xl"],
    borderRadius: borderRadius.xs,
    paddingX: dimensions["lg"], // 12px
    paddingY: dimensions["xs"], // 4px
    fontSize: typographyTokens.size.base,
    fontWeight: typographyTokens.weight.regular,
    lineHeight: 1.5,
    label: {
      fontSize: typographyTokens.size.regular,
      fontWeight: typographyTokens.weight.regular,
    },
    colors: {
      background: colorsFoundation.neutral.white,
      text: colorsFoundation.neutral.softblack,
      placeholder: colorsFoundation.neutral[500],
      border: {
        default: colorsFoundation.neutral[300],
        hover: colorsFoundation.neutral[400],
        focus: colorsFoundation.key.gold[400],
        error: colorsFoundation.status.negative[500],
        disabled: colorsFoundation.neutral[200],
      },
      label: {
        default: colorsFoundation.neutral.softblack,
        focus: colorsFoundation.key.gold[600],
        error: colorsFoundation.status.negative[500],
        disabled: colorsFoundation.neutral[400],
      },
      helperText: {
        default: colorsFoundation.neutral[600],
        error: colorsFoundation.status.negative[500],
      },
    },
    icon: {
      size: "24px",
      color: colorsFoundation.neutral[700],
    },
  },
  checkbox: {
    size: "24px",
    borderRadius: borderRadius.xs,
    colors: {
      border: {
        default: colorsFoundation.neutral[300],
        hover: colorsFoundation.neutral[500],
        checked: colorsFoundation.key.gold[600],
      },
      background: {
        default: colorsFoundation.neutral.white,
        checked: colorsFoundation.key.gold[600],
      },
      checkmark: colorsFoundation.neutral.white,
    },
  },
  toggle: {
    width: "48px",
    height: "28px",
    borderRadius: "12px", // fully rounded
    thumbSize: "20px",
    labelGap: dimensions.md,
    colors: {
      track: {
        off: colorsFoundation.neutral[50],
        on: colorsFoundation.neutral[50],
      },
      thumb: {
        off: colorsFoundation.neutral[500],
        on: colorsFoundation.key.gold[600],
      },
    },
  },
  dataTable: {
    filterInput: {
      fontSize: typographyTokens.size.sm,
      paddingY: dimensions.xs,
      paddingRight: 0,
      placeholderColor: colorsFoundation.neutral[500],
      borderColor: colorsFoundation.gray[200],
      icon: {
        padding: dimensions.xs,
        size: typographyTokens.size.lg,
      },
    },
    row: {
      hoverBackground: colorsFoundation.gray[50],
    },
    // Cool-grey chrome for the click-dummy table look. Shared source of truth
    // for both the DataTable fleet and the header-controls tables (tablePreset).
    paper: {
      borderColor: colorsFoundation.gray[200],
      borderRadius: "10px",
    },
    headCell: {
      background: colorsFoundation.gray[100],
      borderColor: colorsFoundation.gray[200],
      color: colorsFoundation.gray[700],
      fontSize: "12px",
      fontWeight: 600,
    },
    bodyCell: {
      borderColor: colorsFoundation.gray[100],
      fontSize: "0.8125rem", // 13px
    },
    // Square rounded control for MRT filter/column toggle buttons in the header.
    toolbarControl: {
      size: 34,
      borderRadius: dimensions.md,
      color: colorsFoundation.gray[500],
      hoverBg: colorsFoundation.gray[100],
      hoverColor: colorsFoundation.gray[900],
    },
    search: {
      width: 320,
      borderRadius: dimensions.md,
    },
  },
  listItem: {
    paddingY: dimensions["lg"],
    paddingRight: "5px",
    height: "36px",
    actionButton: {
      size: "36px",
      remove: {
        hoverBackground: colorsFoundation.status.negative[50],
        iconColor: colorsFoundation.status.negative[600],
      },
      preview: {
        hoverBackground: colorsFoundation.neutral[50],
        iconColor: colorsFoundation.key.blue[600],
      },
    },
  },
  sumInput: {
    label: {
      fontSize: typographyTokens.size.base,
      fontWeight: typographyTokens.weight.regular,
    },
    value: {
      fontSize: typographyTokens.size.base,
      fontWeight: typographyTokens.weight.medium,
    },
  },
};

export default componentTokens;
